/**
 * Onboarding & org-provisioning DTOs — the over-the-wire contract shared by the
 * API (apps/api/src/modules/onboarding.ts) and the web client
 * (apps/web/src/lib/onboarding-api.ts).
 *
 * Covers: public self-serve signup, org settings (read/update), and sites CRUD.
 */

// ============================== SIGNUP ==============================

/**
 * Public self-serve signup payload. The slug becomes the org's globally-unique
 * subdomain/handle; the email becomes the Org Owner's login. All fields are
 * re-validated server-side (slug regex, password strength, email format).
 */
export interface SignupInput {
  /** Org display name, e.g. "Grace Community Church". */
  orgName: string
  /** Globally-unique slug: lower-case letters, digits, hyphens; 3-63 chars. */
  slug: string
  adminEmail: string
  /** Plaintext password (hashed server-side with argon2id; never persisted raw). */
  password: string
  /** IANA timezone, e.g. "America/Chicago". Defaults to UTC server-side. */
  timezone?: string
}

/** Constraints exposed to the client so signup validation matches the server. */
export const SLUG_PATTERN = '^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?$'
export const SLUG_MIN = 3
export const SLUG_MAX = 63
export const PASSWORD_MIN = 10
export const PASSWORD_MAX = 1024

/** Slugs that must not be claimed (collide with routes / reserved words). */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'www',
  'app',
  'api',
  'admin',
  'login',
  'signup',
  'settings',
  'static',
  'assets',
  'public',
  'health',
  'support',
  'help',
  'mail',
  'billing',
])

/** Normalize a raw slug input to the canonical lower-case, hyphenated form. */
export function normalizeSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX)
}

export interface SlugValidation {
  ok: boolean
  /** A human-readable reason when !ok, else null. */
  reason: string | null
}

/** Client + server shared slug check (shape, length, reserved words). */
export function validateSlug(slug: string): SlugValidation {
  if (slug.length < SLUG_MIN) return { ok: false, reason: `At least ${SLUG_MIN} characters.` }
  if (slug.length > SLUG_MAX) return { ok: false, reason: `At most ${SLUG_MAX} characters.` }
  if (!new RegExp(SLUG_PATTERN).test(slug))
    return {
      ok: false,
      reason: 'Use lowercase letters, numbers, and hyphens (cannot start/end with a hyphen).',
    }
  if (RESERVED_SLUGS.has(slug)) return { ok: false, reason: 'That handle is reserved.' }
  return { ok: true, reason: null }
}

/** Lightweight password-strength gate shared by client and server. */
export function passwordIssues(password: string): string[] {
  const issues: string[] = []
  if (password.length < PASSWORD_MIN) issues.push(`At least ${PASSWORD_MIN} characters.`)
  if (!/[a-z]/.test(password)) issues.push('Include a lowercase letter.')
  if (!/[A-Z]/.test(password)) issues.push('Include an uppercase letter.')
  if (!/[0-9]/.test(password)) issues.push('Include a number.')
  return issues
}

// ============================== ORG SETTINGS ==============================

/** Org branding settings persisted in `orgs.settings.branding`. */
export interface OrgBranding {
  logoUrl: string | null
  primaryColor: string | null
  accentColor: string | null
  tagline: string | null
}

/** Org settings as returned by GET /onboarding/org. */
export interface OrgSettings {
  id: string
  slug: string
  name: string
  legalName: string | null
  timezone: string
  locale: string
  currency: string
  status: string
  branding: OrgBranding
  createdAt: string
}

/** PATCH /onboarding/org payload. All fields optional (partial update). */
export interface UpdateOrgInput {
  name?: string
  legalName?: string | null
  timezone?: string
  locale?: string
  currency?: string
  branding?: Partial<OrgBranding>
}

// ============================== SITES ==============================

export interface SiteDto {
  id: string
  name: string
  slug: string
  timezone: string | null
  phone: string | null
  isPrimary: boolean
  /** Free-form address object (street/city/state/postal/country). */
  address: Record<string, unknown>
  createdAt: string
}

export interface NewSiteInput {
  name: string
  slug: string
  timezone?: string | null
  phone?: string | null
  isPrimary?: boolean
  address?: Record<string, unknown>
}

export interface UpdateSiteInput {
  name?: string
  slug?: string
  timezone?: string | null
  phone?: string | null
  isPrimary?: boolean
  address?: Record<string, unknown>
}
